import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  IconButton,
  Popper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import { textColorHandler } from "../Helpers";

interface AutocompleteFieldProps {
  name: string;
  control: any;
  label: string;
  options: any[];
  required?: boolean;
  multiple?: boolean;
  labelIdentifier?: string | string[];
  sortOrder?: "asc" | "desc";
  onCreateClick?: () => void;
  onEditClick?: (selectedItem: any) => void;
  onInfoClick?: (selectedItem: any) => void;
}

const AUTOCOMPLETE_POPPER_Z_INDEX = 2147483647;

const AutocompletePopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement={props.placement ?? "bottom-start"}
      modifiers={[
        {
          name: "flip",
          enabled: true,
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            altAxis: true,
            tether: false,
            rootBoundary: "viewport",
          },
        },
        ...(props.modifiers ?? []),
      ]}
      sx={{
        zIndex: AUTOCOMPLETE_POPPER_Z_INDEX,
        ...props.sx,
      }}
    />
  );
};

const getLabelFromDataItem = (
  dataItem: any,
  labelIdentifier?: string | string[]
): string => {
  if (!dataItem) {
    return "";
  }

  if (!labelIdentifier) {
    return dataItem?.name ?? dataItem?.label ?? dataItem?.title ?? "";
  }

  if (Array.isArray(labelIdentifier)) {
    return labelIdentifier
      .map((key) => dataItem?.[key] ?? "")
      .filter(Boolean)
      .join(" ");
  }

  return dataItem?.[labelIdentifier] ?? "";
};

const extractNum = (s: string): number | null => {
  const m = s.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

const formatOptions = (
  options: any[],
  labelIdentifier?: string | string[],
  sortOrder: "asc" | "desc" = "asc"
) => {
  const direction = sortOrder === "asc" ? 1 : -1;

  const formatted = options
    .filter(Boolean)
    .map((item, index) => {
      const id = item?.["@id"] ?? item?.id ?? `option-${index}`;
      const label = getLabelFromDataItem(item, labelIdentifier);

      return {
        id,
        label,
        color: item?.color,
        raw: item,
      };
    });

  formatted.sort((optionA, optionB) => {
    const labelNumberA = extractNum(optionA.label);
    const labelNumberB = extractNum(optionB.label);

    if (labelNumberA !== null && labelNumberB !== null) {
      if (labelNumberA !== labelNumberB) {
        return direction * (labelNumberA - labelNumberB);
      }

      return direction * optionA.label.localeCompare(optionB.label);
    }

    if (labelNumberA !== null) {
      return direction * -1;
    }

    if (labelNumberB !== null) {
      return direction * 1;
    }

    return direction * optionA.label.localeCompare(optionB.label);
  });

  return formatted;
};

const findOptionById = (formattedOptions: any[], value: any) => {
  const id =
    typeof value === "object" && value !== null
      ? value["@id"] ?? value.id
      : value;

  return formattedOptions.find((option) => option.id === id);
};

export const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  name,
  control,
  label,
  options,
  required = false,
  multiple = false,
  labelIdentifier,
  sortOrder = "asc",
  onCreateClick,
  onEditClick,
  onInfoClick,
}) => {
  const optionsArray = Array.isArray(options) ? options : [];

  const formattedOptions = useMemo(
    () => formatOptions(optionsArray, labelIdentifier, sortOrder),
    [optionsArray, labelIdentifier, sortOrder]
  );

  const renderOptionLabelChip = (optionLabel: string, color?: string) => {
    if (!color) {
      return optionLabel;
    }

    return (
      <Chip
        size="small"
        label={optionLabel}
        sx={{
          backgroundColor: `${color} !important`,
          color: `${textColorHandler(color)} !important`,
        }}
      />
    );
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={multiple ? [] : ""}
      rules={{
        required: required ? `${label} is required` : false,
      }}
      render={({ field: { onChange, value }, fieldState }) => {
        const currentValue = multiple
          ? Array.isArray(value)
            ? value
                .map((val) => findOptionById(formattedOptions, val))
                .filter(Boolean)
            : []
          : findOptionById(formattedOptions, value) ?? null;

        const handleChange = (_: any, newValue: any) => {
          const selected = multiple
            ? Array.isArray(newValue)
              ? newValue.map((val: any) => val.id)
              : []
            : newValue?.id || "";

          onChange(selected);
        };

        const selectedSingleItem = !multiple
          ? currentValue?.raw
          : Array.isArray(currentValue) && currentValue.length === 1
            ? currentValue[0].raw
            : null;

        return (
          <Box display="flex" alignItems="center" gap="4px" width="100%">
            <Autocomplete
              multiple={multiple}
              options={formattedOptions}
              getOptionLabel={(option) => option?.label ?? ""}
              value={currentValue || (multiple ? [] : null)}
              isOptionEqualToValue={(option, selectedValue) =>
                option.id === selectedValue.id
              }
              onChange={handleChange}
              PopperComponent={AutocompletePopper}
              ListboxProps={{
                sx: {
                  maxHeight: 320,
                  backgroundColor: "background.paper",
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                },
              }}
              componentsProps={{
                paper: {
                  sx: {
                    zIndex: AUTOCOMPLETE_POPPER_Z_INDEX,
                    backgroundColor: "background.paper",
                    boxShadow: 6,
                    borderRadius: 1,
                  },
                },
                popper: {
                  sx: {
                    zIndex: AUTOCOMPLETE_POPPER_Z_INDEX,
                  },
                },
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {renderOptionLabelChip(option.label, option.color)}
                </li>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const tagProps = getTagProps({ index });

                  return (
                    <Chip
                      {...tagProps}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  required={required}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  sx={{
                    "& .MuiAutocomplete-tag": {
                      height: 24,
                      fontSize: "0.75rem",
                      padding: "0 4px",
                    },
                    "& .MuiInputBase-root": {
                      padding: "1px",
                    },
                    "& .MuiFormLabel-root": {
                      top: -6,
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      alignItems: "center",
                    },
                  }}
                />
              )}
              sx={{ flexGrow: 1 }}
            />

            {!selectedSingleItem && onCreateClick && (
              <IconButton
                color="primary"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onCreateClick();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}

            {onEditClick && selectedSingleItem && (
              <IconButton
                color="secondary"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onEditClick(selectedSingleItem);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}

            {onInfoClick && selectedSingleItem && (
              <IconButton
                color="primary"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onInfoClick(selectedSingleItem);
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }}
    />
  );
};